import { useState, type FormEvent } from 'react';
import { REPOSITORY_ID_SOURCE } from '@athenaeum/protocol/sync';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, post, type Repo } from '../../services/api';
import { RequireUser } from '../../components/RequireUser';
import { StartFromVaultSteps, TextOnlyNotice, serverUrl } from '../../components/SyncGuide';
import { ErrorBox, PageHeader } from '../../components/Ui';
import { Icon } from '../../components/Icon';
import { useTitle } from '../../hooks';
import { WIKI_NAME_PATTERN } from '../../shared/utils/validation';

/**
 * The protocol's ID pattern, with the trailing `-` of its character class escaped: browsers compile the
 * `pattern` attribute with the `v` flag, which rejects a bare `-` there and then skips validation silently.
 */
export const REPOSITORY_ID_INPUT_PATTERN = REPOSITORY_ID_SOURCE.replace(/-\]/g, '\\-]');

export function NewRepo() {
  useTitle('New wiki');
  return <RequireUser>{(user) => <NewRepoForm username={user.username} />}</RequireUser>;
}

function NewRepoForm({ username }: { username: string }) {
  const navigate = useNavigate();
  const server = serverUrl();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [topics, setTopics] = useState('');
  const [localId, setLocalId] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const [error, setError] = useState<ApiError>();
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const offeredId = localId.trim();
      const repo = await post<Repo>('/repositories', {
        name: name.trim(),
        description,
        visibility,
        topics: topics
          .split(',')
          .map((topic) => topic.trim())
          .filter(Boolean),
        ...(offeredId ? { id: offeredId } : {}),
      });
      navigate(`/${repo.owner.username}/${repo.name}`);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'error', 'Something went wrong.'));
      setBusy(false);
    }
  };

  return (
    <div className="container stack">
      <PageHeader
        title="Reserve a Library wiki"
        description="A wiki holds one knowledge base as plain text, with its full history."
      />
      <div className="creation-layout">
        <form className="card card-pad settings-panel" onSubmit={submit}>
          {error && <ErrorBox error={error} />}
          <div className="field">
            <label htmlFor="name">Wiki name</label>
            <div className="row wiki-name-input">
              <span className="muted">{username} /</span>
              <input
                id="name"
                className="input"
                required
                autoFocus
                maxLength={100}
                pattern={WIKI_NAME_PATTERN}
                title="Start with a letter or digit, then letters, digits, . _ or -"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-describedby="name-hint"
                aria-invalid={error?.status === 409 || undefined}
              />
            </div>
            <span className="hint" id="name-hint">
              Start with a letter or digit; then letters, digits, <code>.</code> <code>_</code> <code>-</code>
              .
            </span>
          </div>
          <div className="field">
            <label htmlFor="description">
              Description <span className="muted">(optional)</span>
            </label>
            <input
              id="description"
              className="input"
              maxLength={350}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-topics">
              Topics <span className="muted">(optional)</span>
            </label>
            <input
              id="new-topics"
              className="input"
              maxLength={650}
              value={topics}
              onChange={(event) => setTopics(event.target.value)}
              placeholder="research, field-notes"
              aria-describedby="new-topics-hint"
            />
            <span className="hint" id="new-topics-hint">
              Separate up to 12 tags with commas. Use letters, numbers, and hyphens.
            </span>
          </div>
          <div className="field">
            <label htmlFor="local-id">
              Local wiki ID <span className="muted">(optional, for a vault you already have)</span>
            </label>
            <input
              id="local-id"
              className="input mono"
              pattern={REPOSITORY_ID_INPUT_PATTERN}
              value={localId}
              onChange={(e) => setLocalId(e.target.value)}
              aria-describedby="local-id-hint"
              placeholder="repo_…"
              autoComplete="off"
            />
            <span className="hint" id="local-id-hint">
              Paste the <code>repositoryId</code> from the vault’s <code>athenaeum.json</code> to give this
              wiki the same ID. Then, inside the vault, run{' '}
              <code>
                athenaeum sync link --server {server} --remote {username}/{name.trim() || '<name>'}
              </code>
              . If the ID is already taken here, creation is refused; leave this blank to create a new wiki
              identity. You do not need it with <code>athenaeum sync link --create</code>, which offers the
              vault’s ID itself.
            </span>
          </div>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
            <legend className="label" style={{ marginBottom: 6 }}>
              Visibility
            </legend>
            <div className="stack" style={{ marginTop: 0 }}>
              <label className="radio-card">
                <input
                  type="radio"
                  name="visibility"
                  checked={visibility === 'private'}
                  onChange={() => setVisibility('private')}
                />
                <span>
                  <strong>Private</strong>
                  <span className="hint">Only you and the people you add can see it.</span>
                </span>
              </label>
              <label className="radio-card">
                <input
                  type="radio"
                  name="visibility"
                  checked={visibility === 'public'}
                  onChange={() => setVisibility('public')}
                />
                <span>
                  <strong>Public</strong>
                  <span className="hint">
                    Anyone on the internet, signed in or not, can read every page and every revision you push
                    here, and can copy it. Only you and the collaborators you add can change it.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>
          <div style={{ marginBottom: 16 }}>
            <TextOnlyNotice />
          </div>
          <div className="row">
            <button className="btn primary" disabled={busy || !name.trim()}>
              <Icon name="plus" />
              {busy ? 'Creating…' : 'Reserve wiki'}
            </button>
            <Link className="btn" to="/settings/wikis">
              Cancel
            </Link>
          </div>
        </form>
        <section className="card card-pad stack repo-local-guide" aria-labelledby="terminal-heading">
          <Icon name="terminal" />
          <h2 id="terminal-heading" style={{ margin: 0, fontSize: '1.05rem' }}>
            Or create it from the terminal
          </h2>
          <p className="muted" style={{ margin: 0 }}>
            If the vault is already on your computer, this makes the wiki and links the folder in one go.
          </p>
          <StartFromVaultSteps server={server} name={name} />
        </section>
      </div>
    </div>
  );
}
